<template>
  <div class="add-solution">
    <div class="form-content">
      <el-form>
        <el-row>
          <el-col :span="12">
            <el-form-item label="语言" required>
              <el-select v-model="currentData.language_code" placeholder="请选择语言">
                <el-option v-for="item in languageList" :key="item.code" :label="item.name" :value="item.code" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="首页展示">
              <el-switch
                v-model="currentData.is_home"
                :active-value="1"
                :inactive-value="0"
                active-color="#409EFF"
                inactive-color="#dcdfe6"
              />
              <span style="margin-left: 10px; font-size: 12px; color: #909399;">
                （最多展示 3 条）
              </span>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 标题 -->
        <el-form-item label="标题" required>
          <el-input v-model="currentData.title" placeholder="请输入解决方案标题" />
        </el-form-item>

        <!-- 副标题 -->
        <el-form-item label="副标题">
          <el-input v-model="currentData.sub_title" placeholder="请输入副标题（选填）" />
        </el-form-item>

        <!-- 排序 -->
        <el-form-item label="排序">
          <el-input-number v-model="currentData.sort" :min="0" :max="9999" controls-position="right" />
          <span style="margin-left: 10px; font-size: 12px; color: #909399;">数值越小越靠前</span>
        </el-form-item>

        <!-- 封面图 -->
        <el-form-item label="封面图" required>
          <div class="upload-container">
            <upload
              :path="'solution'"
              :images="currentData.cover"
              :multiple="false"
              :num="1"
              @success="handleCoverSuccess"
              @remove="handleCoverRemove"
            />
          </div>
          <div class="upload-tip">
            <i class="el-icon-info" />
            <span>建议上传 1440×640 像素的图片，避免被裁剪</span>
          </div>
        </el-form-item>

        <!-- 特色（JSON 编辑） -->
        <el-form-item label="特色">
          <div class="characteristic-editor">
            <div class="characteristic-list">
              <div v-for="(item, index) in characteristicList" :key="index" class="characteristic-item">
                <el-input
                  v-model="characteristicList[index]"
                  placeholder="请输入特色项"
                  style="flex: 1;"
                >
                  <el-button slot="append" icon="el-icon-delete" @click="removeCharacteristic(index)" />
                </el-input>
              </div>
            </div>
            <el-button
              type="text"
              icon="el-icon-plus"
              @click="addCharacteristic"
              style="margin-top: 8px;"
            >
              添加特色项
            </el-button>
          </div>
        </el-form-item>

        <!-- 案例（JSON 编辑） -->
        <el-form-item label="案例">
          <div class="cases-editor">
            <div class="case-cards">
              <div v-for="(item, index) in casesList" :key="index" class="case-card">
                <!-- 图片区域 -->
                <div class="case-image-box">
                  <el-image
                    v-if="item.image"
                    :src="item.image"
                    fit="cover"
                    class="case-img"
                  />
                  <div v-else class="case-img-placeholder">
                    <i class="el-icon-picture-outline" />
                  </div>
                  <!-- 悬停操作蒙版 -->
                  <div v-if="item.image" class="case-image-overlay">
                    <div class="overlay-actions">
                      <span class="overlay-btn" @click="previewImage(item.image)">
                        <i class="el-icon-view" />
                      </span>
                      <span class="overlay-btn">
                        <el-upload
                          class="overlay-upload"
                          action="#"
                          :auto-upload="true"
                          :show-file-list="false"
                          :http-request="(options) => customUpload(options, index)"
                          accept="image/*"
                        >
                          <span><i class="el-icon-refresh" /></span>
                        </el-upload>
                      </span>
                      <span class="overlay-btn overlay-btn-danger" @click="handleCaseImageRemove(index)">
                        <i class="el-icon-delete" />
                      </span>
                    </div>
                  </div>
                  <!-- 上传按钮（无图片时显示） -->
                  <el-upload
                    v-if="qiniuToken && !item.image"
                    class="case-upload-btn"
                    action="#"
                    :auto-upload="true"
                    :show-file-list="false"
                    :http-request="(options) => customUpload(options, index)"
                    accept="image/*"
                  >
                    <el-button type="text" size="small">上传图片</el-button>
                  </el-upload>
                  <el-button v-else-if="!item.image" type="text" size="small" disabled>加载中...</el-button>
                </div>
                <!-- 标题输入 -->
                <div class="case-info">
                  <el-input v-model="item.title" placeholder="案例标题" size="small" />
                </div>
              </div>
              <!-- 添加按钮 -->
              <div class="case-card case-card-add" @click="addCase">
                <i class="el-icon-plus" />
                <span>添加案例</span>
              </div>
            </div>
          </div>
        </el-form-item>

        <!-- 内容 -->
        <el-form-item label="内容" required>
          <div class="rich-text-editor-container">
            <rich-text-editor
              v-model="currentData.content"
              :path="'solution'"
              :placeholder="placeholders[currentData.language_code] || '请输入内容'"
              @change="handleContentChange"
            />
          </div>
        </el-form-item>
      </el-form>
    </div>

    <!-- 表单操作 -->
    <div class="form-actions">
      <el-button @click="$emit('cancel')">取消</el-button>
      <el-button type="primary" @click="handleSubmit">保存</el-button>
    </div>
  </div>
</template>

<script>
import { getLanguage, getQiniuToken } from '@/api/public'
import RichTextEditor from '@/components/tool/RichTextEditor'
import Upload from '@/components/tool/lib/upload/index.vue'
import { addSolution, updateSolution } from '@/api/website'
import qiniuUploader from '@/util/qiniuUploader.js'

export default {
  name: 'AddSolution',
  components: {
    RichTextEditor,
    Upload
  },
  props: {
    solution: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      languageList: [],
      characteristicList: [''],
      casesList: [], // 案例列表 [{title, image}]
      qiniuToken: '',
      qiniuDomain: 'https://cdn.puditech.com',
      qiniuUploadUrl: 'https://up-z0.qiniup.com',
      caseUploadData: { token: '', key: '' },
      currentData: {
        language_code: 'zh',
        title: '',
        sub_title: '',
        cover: '',
        characteristic: '[]',
        cases: '[]',
        content: '',
        is_home: 0,
        sort: 0
      },
      placeholders: {
        zh: '请输入解决方案内容',
        en: 'Please enter solution content',
        ko: '솔루션 내용을 입력하세요'
      }
    }
  },
  watch: {
    solution: {
      handler(newSolution) {
        if (newSolution) {
          this.resetForm()
          this.currentData = {
            id: newSolution.id,
            language_code: newSolution.language_code || 'zh',
            title: newSolution.title || '',
            sub_title: newSolution.sub_title || '',
            cover: newSolution.cover || '',
            characteristic: newSolution.characteristic || '[]',
            cases: newSolution.cases || '[]',
            content: newSolution.content || '',
            is_home: newSolution.is_home || 0,
            sort: newSolution.sort || 0
          }
          // 解析特色 JSON
          try {
            const parsed = JSON.parse(newSolution.characteristic || '[]')
            if (Array.isArray(parsed) && parsed.length > 0) {
              this.characteristicList = parsed
            } else {
              this.characteristicList = ['']
            }
          } catch (e) {
            this.characteristicList = ['']
          }
          // 解析案例 JSON
          try {
            const parsedCases = JSON.parse(newSolution.cases || '[]')
            if (Array.isArray(parsedCases)) {
              this.casesList = parsedCases
            } else {
              this.casesList = []
            }
          } catch (e) {
            this.casesList = []
          }
        } else {
          this.resetForm()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.getLanguageList()
    this.getQiniuToken()
  },
  methods: {
    async getLanguageList() {
      try {
        const res = await getLanguage()
        this.languageList = res.data || []
      } catch (error) {
        console.error('获取语言列表失败:', error)
      }
    },
    async getQiniuToken() {
      try {
        const res = await getQiniuToken()
        this.qiniuToken = res.data
        qiniuUploader.init({
          region: 'CXC',
          uptoken: res.data,
          domain: this.qiniuDomain,
          shouldUseQiniuFileName: true,
          key: 'solution/case/'
        })
      } catch (error) {
        console.error('获取七牛Token失败:', error)
      }
    },
    resetForm() {
      this.characteristicList = ['']
      this.casesList = []
      this.currentData = {
        language_code: 'zh',
        title: '',
        sub_title: '',
        cover: '',
        characteristic: '[]',
        cases: '[]',
        content: '',
        is_home: 0,
        sort: 0
      }
    },
    addCase() {
      this.casesList.push({ title: '', image: '' })
    },
    removeCase(index) {
      this.casesList.splice(index, 1)
    },
    previewImage(url) {
      this.$alert('<img src="' + url + '" style="max-width:100%;max-height:70vh;display:block;margin:0 auto;">', '查看图片', {
        dangerouslyUseHTMLString: true,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: '关闭',
        customClass: 'image-preview-dialog'
      })
    },
    handleCaseUploadSuccess(index, res) {
      if (res && res.key) {
        this.casesList[index].image = this.qiniuDomain + '/' + res.key
      } else {
        this.$message.error('上传失败')
      }
    },
    handleCaseImageRemove(index) {
      this.casesList[index].image = ''
    },
    async customUpload(options, index) {
      const { file } = options
      
      // 校验
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('请上传图片文件')
        return
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return
      }
      if (!this.qiniuToken) {
        this.$message.error('上传配置加载中，请稍后重试')
        return
      }
      
      // 生成 key
      const suffix = file.name.substring(file.name.lastIndexOf('.') + 1)
      const key = 'solution/case/' + this.createNonceStr() + '.' + suffix
      
      // 构造 FormData
      const formData = new FormData()
      formData.append('token', this.qiniuToken)
      formData.append('key', key)
      formData.append('file', file)
      
      try {
        const response = await fetch(this.qiniuUploadUrl, {
          method: 'POST',
          body: formData
        })
        const result = await response.json()
        if (result.key) {
          // 直接设置图片 URL
          this.$set(this.casesList[index], 'image', this.qiniuDomain + '/' + result.key)
          this.$message.success('上传成功')
        } else {
          this.$message.error('上传失败')
        }
      } catch (err) {
        console.error('上传失败:', err)
        this.$message.error('上传失败')
      }
    },
    createNonceStr() {
      const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
      let res = ''
      for (let i = 0; i < 16; i++) {
        res += chars[Math.floor(Math.random() * chars.length)]
      }
      return res
    },
    handleCoverSuccess(images) {
      this.currentData.cover = images[0]
    },
    handleCoverRemove(images) {
      this.currentData.cover = images.length > 0 ? images[0] : ''
    },
    addCharacteristic() {
      this.characteristicList.push('')
    },
    removeCharacteristic(index) {
      if (this.characteristicList.length <= 1) {
        this.characteristicList = ['']
      } else {
        this.characteristicList.splice(index, 1)
      }
    },
    handleContentChange(content) {
      this.currentData.content = content
    },
    handleSubmit() {
      // 验证
      if (!this.currentData.language_code) {
        this.$message.error('请选择语言')
        return
      }
      if (!this.currentData.title) {
        this.$message.error('请输入标题')
        return
      }
      if (!this.currentData.cover) {
        this.$message.error('请上传封面图')
        return
      }
      if (!this.currentData.content) {
        this.$message.error('请输入内容')
        return
      }

      // 序列化特色
      const characteristics = this.characteristicList.filter(item => item && item.trim() !== '')
      this.currentData.characteristic = JSON.stringify(characteristics)

      // 序列化案例（过滤掉既没有标题又没有图片的）
      const validCases = this.casesList.filter(item => (item.title && item.title.trim() !== '') || item.image)
      this.currentData.cases = JSON.stringify(validCases)

      // 提交
      const submitData = { ...this.currentData }
      const isEdit = submitData.id

      const apiCall = isEdit
        ? updateSolution(submitData.id, submitData)
        : addSolution(submitData)

      apiCall.then(res => {
        if (res.code === 200) {
          this.resetForm()
          this.$emit('save', true)
        } else {
          this.$message.error(res.msg || '保存失败')
        }
      }).catch(() => {
        this.$message.error('保存失败')
      })
    }
  }
}
</script>

<style scoped lang="scss">
.add-solution {
  box-sizing: border-box;
  padding: 0 20px;
}

.form-content {
  box-sizing: border-box;
  margin-bottom: 30px;
}

.characteristic-editor {
  width: 100%;
}

.characteristic-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.characteristic-item {
  display: flex;
  align-items: center;
}

.cases-editor {
  width: 100%;
}

.case-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.case-card {
  width: 140px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s;
}

.case-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.case-image-box {
  width: 100%;
  height: 100px;
  background: #f5f7fa;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.case-img {
  width: 100%;
  height: 100%;
  cursor: zoom-in;
}

.case-img:hover {
  opacity: 0.9;
}

.case-img-placeholder {
  color: #c0c4cc;
  font-size: 28px;
}

.case-upload-btn {
  position: absolute;
  bottom: 4px;
  right: 4px;
}

.case-upload-btn ::v-deep .el-button {
  padding: 2px 8px;
  font-size: 12px;
}

.case-remove-img {
  position: absolute;
  bottom: 4px;
  left: 4px;
  color: #f56c6c;
  padding: 2px 8px;
  font-size: 12px;
}

.case-info {
  padding: 8px;
}

.case-info ::v-deep .el-input__inner {
  border: none;
  padding: 0;
  background: transparent;
}

.case-image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.case-image-box:hover .case-image-overlay {
  opacity: 1;
}

.overlay-actions {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.overlay-btn {
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-btn:hover {
  background: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.overlay-btn-danger:hover {
  background: rgba(245, 108, 108, 0.9);
}

.overlay-upload {
  display: inline;
}

.overlay-upload ::v-deep .el-upload {
  display: inline;
}

.overlay-upload span {
  color: #fff;
}

.case-card-add {
  height: 148px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #909399;
  border-style: dashed;
}

.case-card-add:hover {
  color: #409eff;
  border-color: #409eff;
}

.case-card-add i {
  font-size: 24px;
  margin-bottom: 8px;
}

.rich-text-editor-container {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background-color: #f9f9f9;
  padding: 10px;
  min-height: 300px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f2f5;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 响应式 */
@media (max-width: 768px) {
  .add-solution {
    padding: 10px 0;
  }

  .form-content {
    margin-bottom: 20px;
  }

  .rich-text-editor-container {
    min-height: 200px;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions .el-button {
    width: 100%;
  }

  .quill-editor {
    height: 150px;
  }

  .ql-toolbar.ql-snow {
    flex-wrap: wrap;
  }

  .ql-toolbar.ql-snow .ql-formats {
    margin-right: 10px;
    margin-bottom: 5px;
  }
}
</style>
